import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://oauth.remote-assistant.ai';

class ConfigService {
  async getAuthToken() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('No valid token found');
      }
      return token;
    } catch (error) {
      console.error('Auth token error:', error);
      throw new Error('Authentication required');
    }
  }

  async createBusinessConfig(phoneNumber, businessName, website, address) {
    const token = await this.getAuthToken();
    
    const payload = {
      phone_number: phoneNumber,
      business_name: businessName,
      website: website,
      address: address
    };
    
    console.log('API Payload being sent:', payload);
    
    const response = await fetch(`${API_BASE_URL}/oauth/config/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to create business config');
      }
      throw new Error(`Failed to create business config (${response.status})`);
    }

    return await response.json();
  }

  async generateBusinessDescription() {
    const cognitoUserId = this.getUserId();
    if (!cognitoUserId) {
      throw new Error('User not authenticated');
    }
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognitoUserId}/generate-description`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to generate description');
      }
      if (response.status === 404) {
        throw new Error('Business config not found. Please create config first');
      }
      throw new Error(`Failed to generate description (${response.status})`);
    }

    return await response.json();
  }

  async updateBusinessDescription(businessDescription, assistantName) {
    const cognitoUserId = this.getUserId();
    if (!cognitoUserId) {
      throw new Error('User not authenticated');
    }
    const token = await this.getAuthToken();
    
    const updateData = {
      business_description: businessDescription
    };

    if (assistantName) {
      updateData.assistant_name = assistantName;
    }

    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognitoUserId}/config`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to update config');
      }
      if (response.status === 404) {
        throw new Error('Business config not found');
      }
      throw new Error(`Failed to update config (${response.status})`);
    }

    return await response.json();
  }

  async updateConfigDescription(phoneNumber, businessDescription, assistantName, personalityTraits) {
    const token = await this.getAuthToken();
    
    const updateData = {
      phone_number: phoneNumber
    };

    if (businessDescription) updateData.business_description = businessDescription;
    if (assistantName) updateData.assistant_name = assistantName;
    if (personalityTraits) {
      updateData.formality = personalityTraits.formality;
      updateData.verbosity = personalityTraits.verbosity;
      updateData.empathy = personalityTraits.empathy;
      updateData.proactivity = personalityTraits.proactivity;
      updateData.humor = personalityTraits.humor;
    }

    const response = await fetch(`${API_BASE_URL}/oauth/config/update-description`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to update config');
      }
      if (response.status === 404) {
        throw new Error('Business config not found');
      }
      throw new Error(`Failed to update config (${response.status})`);
    }

    return await response.json();
  }

  async updatePersonalityTraits(personalityTraits) {
    console.log('🔧 ConfigService: updatePersonalityTraits called with:', personalityTraits);
    
    const cognitoUserId = this.getUserId();
    console.log('🔧 ConfigService: cognitoUserId:', cognitoUserId);
    
    if (!cognitoUserId) {
      console.log('❌ ConfigService: No cognitoUserId found');
      throw new Error('User not authenticated');
    }
    
    const token = await this.getAuthToken();
    console.log('🔧 ConfigService: Got auth token, length:', token?.length);
    
    const updateData = {
      personality_traits: personalityTraits
    };
    
    const url = `${API_BASE_URL}/oauth/users/${cognitoUserId}/personality`;
    console.log('🔧 ConfigService: Making request to:', url);
    console.log('🔧 ConfigService: Request body:', updateData);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    console.log('🔧 ConfigService: Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('❌ ConfigService: Error response:', errorText);
      
      if (response.status === 401) {
        throw new Error('Please sign in again to update personality traits');
      }
      if (response.status === 404) {
        throw new Error('Business config not found');
      }
      throw new Error(`Failed to update personality traits (${response.status})`);
    }

    const result = await response.json();
    console.log('✅ ConfigService: updatePersonalityTraits result:', result);
    return result;
  }

  async getAvailableVoices() {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/api/voices/available`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to load voices');
      }
      throw new Error(`Failed to load voices (${response.status})`);
    }

    return await response.json();
  }

  async generateVoicePreview(provider, voiceConfig, text) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/api/voices/preview`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        provider: provider,
        voice_config: voiceConfig,
        text: text
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to generate preview');
      }
      throw new Error(`Failed to generate preview (${response.status})`);
    }

    return await response.json();
  }

  async saveVoiceSettings(selectedVoice) {
    console.log('🔧 ConfigService: saveVoiceSettings called with:', selectedVoice);
    
    const cognitoUserId = this.getUserId();
    console.log('🔧 ConfigService: cognitoUserId:', cognitoUserId);
    
    if (!cognitoUserId) {
      console.log('❌ ConfigService: No cognitoUserId found');
      throw new Error('User not authenticated');
    }
    
    const token = await this.getAuthToken();
    console.log('🔧 ConfigService: Got auth token, length:', token?.length);
    
    const url = `${API_BASE_URL}/oauth/users/${cognitoUserId}/voice-settings`;
    console.log('🔧 ConfigService: Making request to:', url);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        selected_voice: selectedVoice
      })
    });

    console.log('🔧 ConfigService: Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('❌ ConfigService: Error response:', errorText);
      
      if (response.status === 401) {
        throw new Error('Please sign in again to save voice settings');
      }
      throw new Error(`Failed to save voice settings (${response.status})`);
    }

    const result = await response.json();
    console.log('✅ ConfigService: saveVoiceSettings result:', result);
    return result;
  }

  async saveAssistantPhone(assistantPhone) {
    console.log('📞 ConfigService: saveAssistantPhone called with:', assistantPhone);
    
    const cognitoUserId = this.getUserId();
    if (!cognitoUserId) {
      throw new Error('User not authenticated');
    }
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognitoUserId}/assistant-phone`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        assistant_phone: assistantPhone
      })
    });

    console.log('📞 ConfigService: Response status:', response.status);
    console.log('📞 ConfigService: Using endpoint:', `${API_BASE_URL}/oauth/users/${cognitoUserId}/assistant-phone`);
    console.log('📞 ConfigService: Request body:', JSON.stringify({ assistant_phone: assistantPhone }));

    if (!response.ok) {
      const errorText = await response.text();
      console.log('❌ ConfigService: Error response:', errorText);
      
      if (response.status === 401) {
        throw new Error('Please sign in again to save assistant phone');
      }
      if (response.status === 404) {
        throw new Error('Business configuration not found. Please complete previous steps first.');
      }
      throw new Error(`Failed to save assistant phone (${response.status})`);
    }

    const result = await response.json();
    console.log('✅ ConfigService: saveAssistantPhone result:', result);
    return result;
  }

  async getConfigurationStatus(phoneNumber) {
    const token = await this.getAuthToken();
    
    console.log('🔍 DEBUG: getConfigurationStatus called with phoneNumber:', phoneNumber);
    
    const response = await fetch(`${API_BASE_URL}/oauth/config/status/${phoneNumber}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('🔍 DEBUG: getConfigurationStatus response status:', response.status);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to load configuration status');
      }
      throw new Error(`Failed to load configuration status (${response.status})`);
    }

    const result = await response.json();
    console.log('🔍 DEBUG: getConfigurationStatus result:', result);
    return result;
  }

  async markStepComplete(phoneNumber, step) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/config/mark-step-complete/${phoneNumber}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ step })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to mark step complete');
      }
      throw new Error(`Failed to mark step complete (${response.status})`);
    }

    return await response.json();
  }

  async getSubscriptionProducts() {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/oauth/subscription/products`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error(`Failed to load products (${response.status})`);
    }
    return await response.json();
  }

  async createCheckoutSession(checkoutData) {
    const token = await this.getAuthToken();
    
    // Extract user_id and business_id from JWT token
    const payload = JSON.parse(atob(token.split('.')[1]));
    const user_id = payload.sub || payload['cognito:username'] || 'unknown';
    const business_id = payload['custom:business_id'];
    
    const requestData = {
      user_id: user_id,
      business_id: business_id,  // Include business_id for webhook tracking
      ...checkoutData,
      metadata: {
        cognito_user_id: user_id,  // Add cognito_user_id to metadata for webhook identification
        ...checkoutData.metadata
      }
    };
    
    const response = await fetch(`${API_BASE_URL}/oauth/subscription/checkout`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    if (!response.ok) {
      throw new Error(`Failed to create checkout session (${response.status})`);
    }
    return await response.json();
  }

  async getSubscriptionStatus() {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/oauth/subscription/status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error(`Failed to load subscription status (${response.status})`);
    }
    return await response.json();
  }

  async getCurrentConfiguration(phoneNumber) {
    const token = await this.getAuthToken();
    
    console.log('🔍 DEBUG: getCurrentConfiguration called for:', phoneNumber);
    
    const response = await fetch(`${API_BASE_URL}/oauth/config/get/${phoneNumber}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('🔍 DEBUG: getCurrentConfiguration response status:', response.status);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to load configuration');
      }
      if (response.status === 404) {
        console.log('🔍 DEBUG: Configuration not found, returning null');
        return null;
      }
      throw new Error(`Failed to load configuration (${response.status})`);
    }

    const result = await response.json();
    console.log('🔍 DEBUG: getCurrentConfiguration result:', result);
    return result;
  }

  getActivePhoneNumber(user) {
    // Return assistant phone if available, otherwise business phone
    return user?.attributes?.['custom:assistant_phone'] || user?.attributes?.phone_number;
  }

  getBusinessPhoneNumber(user) {
    // Return business phone (original phone number)
    return user?.attributes?.['custom:business_phone'] || user?.attributes?.phone_number;
  }

  // New business_id based methods
  async createBusinessConfigByUserId(cognito_user_id, configData) {
    const token = await this.getAuthToken();
    
    console.log('🔍 ConfigService: Creating business config for user:', cognito_user_id);
    console.log('🔍 ConfigService: Config data being sent:', configData);
    console.log('🔍 ConfigService: Website in payload:', configData.website);
    console.log('🔍 ConfigService: Address in payload:', configData.address);
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognito_user_id}/config`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(configData)
    });

    console.log('🔍 ConfigService: Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('🔍 ConfigService: Error response:', errorText);
      throw new Error(`Failed to create business config (${response.status})`);
    }

    const result = await response.json();
    console.log('🔍 ConfigService: Business config API response:', result);
    
    return result;
  }

  async getBusinessConfigByUserId(cognito_user_id) {
    const token = await this.getAuthToken();
    
    console.log('🔍 ConfigService: Loading business config for user:', cognito_user_id);
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognito_user_id}/config`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('🔍 ConfigService: Get config response status:', response.status);

    if (!response.ok) {
      if (response.status === 404) {
        console.log('🔍 ConfigService: Config not found (404) - clearing business_id from JWT');
        await this.clearBusinessId();
        return null;
      }
      throw new Error(`Failed to load business config (${response.status})`);
    }

    const result = await response.json();
    console.log('🔍 ConfigService: Loaded config:', result);
    console.log('🔍 ConfigService: Website in loaded config:', result.website);
    console.log('🔍 ConfigService: Address in loaded config:', result.address);
    
    return result;
  }

  async getConfigurationStatusByUserId(cognito_user_id) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognito_user_id}/status`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load configuration status (${response.status})`);
    }

    return await response.json();
  }

  getUserId() {
    // Extract user ID from current JWT token
    try {
      const token = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
      if (!token) return null;
      
      // Get the actual JWT token
      const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${token}.idToken`;
      const idToken = localStorage.getItem(idTokenKey);
      
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        return payload.sub || payload['cognito:username'];
      }
      return null;
    } catch (error) {
      console.error('Error extracting user ID:', error);
      return null;
    }
  }

  getBusinessId() {
    // Extract business_id from current JWT token
    try {
      const token = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
      if (!token) return null;
      
      // Get the actual JWT token
      const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${token}.idToken`;
      const idToken = localStorage.getItem(idTokenKey);
      
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        return payload['custom:business_id'];
      }
      return null;
    } catch (error) {
      console.error('Error extracting business_id:', error);
      return null;
    }
  }

  async clearBusinessId() {
    try {
      const { updateUserAttributes } = await import('aws-amplify/auth');
      await updateUserAttributes({
        userAttributes: {
          'custom:business_id': ''
        }
      });
      console.log('✅ ConfigService: Cleared business_id from Cognito');
    } catch (error) {
      console.error('❌ ConfigService: Failed to clear business_id:', error);
    }
  }

  async generatePersonalityResponse(scenario, voiceConfig = null) {
    const cognitoUserId = this.getUserId();
    if (!cognitoUserId) {
      throw new Error('User not authenticated');
    }
    const token = await this.getAuthToken();
    
    const requestBody = {
      scenario: scenario
    };
    
    if (voiceConfig) {
      requestBody.voice_config = voiceConfig;
    }
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognitoUserId}/personality-response`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to generate personality response');
      }
      if (response.status === 404) {
        throw new Error('Business config not found');
      }
      throw new Error(`Failed to generate personality response (${response.status})`);
    }

    return await response.json();
  }

  async saveBehaviorSettings(behaviorData) {
    console.log('🔧 ConfigService: saveBehaviorSettings called with:', behaviorData);
    
    const cognitoUserId = this.getUserId();
    if (!cognitoUserId) {
      throw new Error('User not authenticated');
    }
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognitoUserId}/behavior-settings`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(behaviorData)
    });

    console.log('🔧 ConfigService: saveBehaviorSettings response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('❌ ConfigService: Error response:', errorText);
      
      if (response.status === 401) {
        throw new Error('Please sign in again to save behavior settings');
      }
      throw new Error(`Failed to save behavior settings (${response.status})`);
    }

    const result = await response.json();
    console.log('✅ ConfigService: saveBehaviorSettings result:', result);
    return result;
  }

  async getOAuthConnections() {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/connections`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in again to load connections');
      }
      throw new Error(`Failed to load OAuth connections (${response.status})`);
    }

    return await response.json();
  }

  async recordFeatureEngagement(featureType, featureId, featureName, response, context = {}) {
    const token = await this.getAuthToken();
    const endpoint = response === 'interested' ? 'interested' : 'declined';
    
    const apiResponse = await fetch(`${API_BASE_URL}/oauth/feature-engagement/${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        feature_type: featureType,
        feature_id: featureId,
        feature_name: featureName,
        context: context
      })
    });

    if (!apiResponse.ok) {
      throw new Error(`Failed to record engagement (${apiResponse.status})`);
    }

    return await apiResponse.json();
  }

  async selectSubscriptionPlan(planId) {
    const cognitoUserId = this.getUserId();
    if (!cognitoUserId) {
      throw new Error('User not authenticated');
    }
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognitoUserId}/select-plan`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        price_id: planId
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to select plan (${response.status})`);
    }

    return await response.json();
  }

  async getCustomerProfile() {
    const cognitoUserId = this.getUserId();
    if (!cognitoUserId) {
      throw new Error('User not authenticated');
    }
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/users/${cognitoUserId}/customer-profile`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load customer profile (${response.status})`);
    }

    return await response.json();
  }

  // Referral Program Methods
  async createReferral(email) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/referrals/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ referee_email: email })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to create referral (${response.status})`);
    }

    return await response.json();
  }

  async getUserReferrals() {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/referrals/list`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load referrals (${response.status})`);
    }

    return await response.json();
  }

  async cancelReferral(referralId) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/referrals/${referralId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to cancel referral (${response.status})`);
    }

    return await response.json();
  }

  async getReferralLink() {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/referrals/link`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load referral link (${response.status})`);
    }

    return await response.json();
  }

  async trackReferralSignup(referralCode, cognitoUserId) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/referrals/track-signup`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        referral_code: referralCode,
        referee_cognito_id: cognitoUserId
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to track referral signup (${response.status})`);
    }

    return await response.json();
  }

  async sendReferralInvitation(email, referrerName) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/referrals/send-invitation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        referee_email: email,
        referrer_name: referrerName || 'A friend'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to send invitation (${response.status})`);
    }

    return await response.json();
  }

  async joinInternationalWaitlist(email, countryCode, countryName) {
    const response = await fetch(`${API_BASE_URL}/oauth/international-waitlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        country_code: countryCode,
        country_name: countryName
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to join international waitlist (${response.status})`);
    }

    return await response.json();
  }

  async joinBetaWaitlist(email) {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/oauth/beta-waitlist`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to join beta waitlist (${response.status})`);
    }

    return await response.json();
  }
}

export const configService = new ConfigService();
export default configService;